import { useState } from "react";
import { Instagram, Mail, MapPin } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal } from "./Reveal";
import { Wordmark } from "./Brand";

const EMAIL = "atelierhandmadesimona@gmail.com";
const INSTAGRAM = "https://www.instagram.com/nodinohandmade";
const TIKTOK = "https://www.tiktok.com/@nodinohandmade";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M16.5 3c.3 2.2 1.6 3.8 3.7 4v2.6c-1.3.1-2.6-.3-3.7-1v6.3c0 3.2-2.4 5.6-5.5 5.6A5.4 5.4 0 0 1 5.6 15c0-3 2.4-5.4 5.4-5.4.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2A2.7 2.7 0 1 0 13.7 15V3h2.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

export function Contact() {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const titleLines = t.contact.title.split("\n");

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Comandă Nodino: ${name ? name : "comandă personalizată"}`;
    const bodyLines = [message || "", "", name, email].filter(Boolean).join("\n");
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
  };

  const inputCls =
    "w-full bg-transparent border-b border-[var(--ink)]/20 py-4 font-body text-lg placeholder:text-[var(--ink-3)] focus:border-[var(--gold)] focus:outline-none transition-colors";

  return (
    <footer id="contact" className="relative pt-28 md:pt-40 pb-10 bg-[var(--bg-3)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* left: heading + socials */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow mb-6">{t.contact.eyebrow}</p>
              <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tight mb-10">
                {titleLines.map((l, i) => (
                  <span key={i} className="block">
                    {i === 1 ? <em className="italic text-[var(--gold-dark)]">{l}</em> : l}
                  </span>
                ))}
              </h2>

              <div className="space-y-4">
                <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                  <span className="grid place-items-center w-10 h-10 rounded-full border border-[var(--ink)]/20 group-hover:border-[var(--gold)] transition-colors"><Mail size={16} /></span>
                  <span className="font-body link-underline">{EMAIL}</span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-10 h-10 rounded-full border border-[var(--ink)]/20"><MapPin size={16} /></span>
                  <span className="font-body text-[var(--ink-2)]">{t.contact.location}</span>
                </div>
              </div>

              <div className="mt-10">
                <p className="eyebrow mb-4">{t.contact.followUs}</p>
                <div className="flex gap-3">
                  <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="social-instagram" className="grid place-items-center w-12 h-12 rounded-full border border-[var(--ink)]/20 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors duration-500"><Instagram size={18} /></a>
                  <a href={TIKTOK} target="_blank" rel="noreferrer" data-testid="social-tiktok" className="grid place-items-center w-12 h-12 rounded-full border border-[var(--ink)]/20 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors duration-500"><TikTokIcon /></a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* right: form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.12}>
              <p className="text-[var(--ink-2)] leading-relaxed mb-8 max-w-md">{t.contact.sub}</p>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <input data-testid="input-name" className={inputCls} placeholder={t.contact.name} value={name} onChange={(e) => setName(e.target.value)} required />
                <input data-testid="input-email" type="email" className={inputCls} placeholder={t.contact.email} value={email} onChange={(e) => setEmail(e.target.value)} />
                <textarea data-testid="input-message" rows={4} className={`${inputCls} resize-none`} placeholder={t.contact.message} value={message} onChange={(e) => setMessage(e.target.value)} required />
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 bg-[var(--ink)] text-[var(--bg)] rounded-full pl-8 pr-2 py-2.5 hover:bg-[var(--gold-dark)] transition-colors duration-500"
                >
                  <span className="eyebrow text-inherit">{t.contact.send}</span>
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-[var(--bg)] text-[var(--ink)]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </button>
              </form>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 mb-8" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <Wordmark />
          <p className="font-body text-xs tracking-widest text-[var(--ink-3)] text-center">{t.contact.madeWith}</p>
          <p className="font-body text-xs tracking-widest text-[var(--ink-3)]">© {new Date().getFullYear()} NODINO. {t.contact.rights}</p>
        </div>
      </div>
    </footer>
  );
}
